import styled from 'styled-components'
import React, { useState } from 'react'
import IndexContentContainer from '../../index/IndexContentContainer'
import BookResponse from '../BookResponse'
import RowSpacer from '../../../common/styles/RowSpacer'
import BookPageBar from '../../../common/BookPageBar'
import BooksitoutImages from '../../../images/BooksitoutImages'
import { Author } from '../../../common/Author'
import BookReadingSourceModal from './BookReadingSourceModal'
import BooksitoutIcon from '../../../config/BooksitoutIcon'

interface Props {
	book: BookResponse
	url?: string
	isReadingSourceEnabled?: boolean
	onReadingSourceChange?: () => void
}

const BookMineCard: React.FC<Props> = ({ book, url, isReadingSourceEnabled = false, onReadingSourceChange }) => {
	const [rs, setRs] = useState(book.readingSource)
	const [isSourceModalOpen, setIsSourceModalOpen] = useState(false)

	return (
		<>
		<Container href={url ?? `/book/mine/${book.id}`}>
			<IndexContentContainer marginBottom={0} className="hover-effect">
				{isReadingSourceEnabled && <SourceBadgeWrapper>
					{rs ? (
						rs.provider === 'BOUGHT' ? (
							<SetSourceButton
								onClick={e => {
									e.preventDefault()
									e.stopPropagation()
									setIsSourceModalOpen(true)
								}}
							>
								<BooksitoutIcon.bought color="var(--primary-color)" />
							</SetSourceButton>
						) : rs.provider === 'SHOULD_BUY_NEW' ? (
							<SetSourceButton
								onClick={e => {
									e.preventDefault()
									e.stopPropagation()
									setIsSourceModalOpen(true)
								}}
							>
								<BooksitoutIcon.shouldBuy size={18} color="var(--danger)" />
							</SetSourceButton>
						) : (
							<SourceBadge
								onClick={e => {
									e.preventDefault()
									e.stopPropagation()
									setIsSourceModalOpen(true)
								}}
							>
								{rs.imageUrl && <SourceIcon src={rs.imageUrl} />}
								{rs.label}
								{rs.memo && <SourceTooltip>{rs.memo}</SourceTooltip>}
							</SourceBadge>
						)
					) : (
						<SetSourceButton
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								setIsSourceModalOpen(true)
							}}
						>
							<BooksitoutIcon.plus />
						</SetSourceButton>
					)}
				</SourceBadgeWrapper>}

				<ContentContainer>
					<div>
						<Cover src={book.cover != '' && book.cover != null ? book.cover : BooksitoutImages.Placeholder.bookCover} />
					</div>

					<RowSpacer size={12.5} />

					<Title>{book.title}</Title>
					<RowSpacer size={2.5} />

					<Author>{book.author}</Author>

					<BookPageBar book={book} isOneLine={false} />
				</ContentContainer>
			</IndexContentContainer>
		</Container>

		<BookReadingSourceModal
			bookId={book.id}
			isbn={book.isbn}
			isOpen={isSourceModalOpen}
			onClose={() => setIsSourceModalOpen(false)}
			currentProvider={rs?.provider}
			onSuccess={(readingSource) => {
				setRs(readingSource)
				onReadingSourceChange?.()
			}}
		/>
		</>
	)
}

const Container = styled.a`
	padding-left: 2px;
	padding-right: 2px;

    height: 300px;
`

const ContentContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	text-align: center;
	width: 100%;
`

const Cover = styled.img.attrs({
	className: 'img-fluid',
})`
	max-height: 150px;
	min-height: 150px;
	width: auto;
	max-width: 100%;
	object-fit: contain;
	border-radius: 10px;
`

const Title = styled.h1.attrs({
	className: 'clamp-1-line',
})`
	margin-top: 8px;
	font-size: 15px;
	text-align: center;
`

const SourceBadgeWrapper = styled.div`
	position: absolute;
	top: 6px;
	right: 6px;
	z-index: 5;
`

const SourceBadge = styled.div`
	position: relative;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: 11px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 10px;
	background-color: #aaa;
	color: white;
	white-space: nowrap;
	cursor: pointer;

	&:hover {
		opacity: 0.9;
	}
`

const SetSourceButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 22px;
	color: #aaa;
	cursor: pointer;

	&:hover {
		opacity: 0.7;
	}
`

const SourceIcon = styled.img`
	width: 16px;
	height: 16px;
	border-radius: 4px;
`

const SourceTooltip = styled.div`
	display: none;
	position: absolute;
	top: calc(100% + 6px);
	right: 0;
	padding: 6px 10px;
	border-radius: 8px;
	background-color: var(--card-bg-color);
	color: var(--text-color);
	font-size: 12px;
	font-weight: 400;
	white-space: nowrap;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	z-index: 10;

	${SourceBadge}:hover & {
		display: block;
	}
`

export default BookMineCard
